/**
 * Dashboard page component - Comprehensive order monitoring
 * v2.0.0 - Refactored with kamar-plugins style statistics and unified colors
 */

import { useMemo } from 'react';
import { useOrders, useBotStatus, useQueueStatus } from '../hooks/useOrders';
import { toDate, Order } from '../types';
import { BotStatusIndicator } from '../components/BotStatusIndicator';
import { 
  TrendingUp, 
  Clock, 
  CheckCircle, 
  XCircle,
  Package,
  CreditCard,
  BarChart3,
  Calendar
} from 'lucide-react';
import '../styles/Dashboard.css';

// Bot status configuration - matching kamar-plugins botStatusConfig.php
const BOT_STATUS_CONFIG: Record<string, { icon: string; label: string; bg: string; border: string; color: string; desc: string }> = {
  pending: { icon: '⏳', label: 'W kolejce', bg: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)', border: '#6c757d', color: '#495057', desc: 'Oczekuje na przetworzenie' },
  processing: { icon: '🔄', label: 'Przetwarzanie', bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '#f59e0b', color: '#92400e', desc: 'Bot przetwarza' },
  ordered_awaiting_payment: { icon: '💳', label: 'Do opłacenia', bg: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', border: '#3b82f6', color: '#1e40af', desc: 'Czeka na przelew' },
  completed: { icon: '✅', label: 'Zamówione', bg: 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)', border: '#10b981', color: '#065f46', desc: 'Złożone u dostawcy' },
  failed: { icon: '❌', label: 'Błąd', bg: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)', border: '#ef4444', color: '#991b1b', desc: 'Przetwarzanie nie powiodło się' },
  partial: { icon: '⚠️', label: 'Częściowe', bg: 'linear-gradient(135deg, #fef9c3 0%, #fef08a 100%)', border: '#eab308', color: '#854d0e', desc: 'Niektóre produkty niedostępne' },
  reservation: { icon: '📋', label: 'Rezerwacja', bg: 'linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%)', border: '#6366f1', color: '#3730a3', desc: 'Zamówienie rezerwacyjne' },
  ordered_by_woocommerce: { icon: '🔗', label: 'WooCommerce', bg: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)', border: '#9ca3af', color: '#4b5563', desc: 'Zamówione przez WC' },
  product_unavailable: { icon: '📦', label: 'Brak produktu', bg: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '#f59e0b', color: '#92400e', desc: 'Produkt niedostępny' },
};

// Helper to get bot status config
function getBotStatusConfig(status: string) {
  return BOT_STATUS_CONFIG[status] || { icon: '❓', label: 'Nieznany', bg: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)', border: '#9ca3af', color: '#6b7280', desc: 'Status nieznany' };
}

export function Dashboard() {
  const { orders: allOrders } = useOrders(undefined, 1000); // Fetch more orders for accurate stats
  const { botStatus } = useBotStatus();
  const { queueStatus } = useQueueStatus();
  // Calculate "Pozostało do zamówienia" statistics - matching kamar-plugins logic
  const remainingStats = useMemo(() => {
    // Filter orders that need to be ordered: wc_status = 'processing' AND no supplier_order_id
    const ordersToBeOrdered = allOrders.filter(o => 
      (o.wc_status === 'processing' || o.wc_status === 'wc-processing' || !o.wc_status) &&
      !o.supplier_order_id
    );

    // Opłacone (prepaid) - payment_status = 'paid' or payment method is not COD/BACS
    const prepaidOrders = ordersToBeOrdered.filter(o => 
      o.payment_status === 'paid' || 
      (o.wc_meta?.payment_method && !['cod', 'bacs', 'bank_transfer'].includes(o.wc_meta.payment_method))
    );

    // Za pobraniem (COD) - payment method = 'cod'
    const codOrders = ordersToBeOrdered.filter(o => 
      o.wc_meta?.payment_method === 'cod'
    );

    // DHL - shipping method contains 'DHL' or 'dhl'
    const dhlOrders = ordersToBeOrdered.filter(o => {
      const shippingMethod = o.wc_meta?.shipping_method || o.shipping?.delivery_method || '';
      return shippingMethod.toLowerCase().includes('dhl') || shippingMethod === 'kurier';
    });

    // Rezerwacje (reservations) - payment_status = 'reservation'
    const reservationOrders = ordersToBeOrdered.filter(o => 
      o.payment_status === 'reservation'
    );

    // Calculate totals (using total_amount from Firebase)
    const calculateTotal = (orders: Order[]) => 
      orders.reduce((sum, o) => sum + (o.total_amount || 0), 0);

    return {
      total: ordersToBeOrdered.length,
      totalAmount: calculateTotal(ordersToBeOrdered),
      prepaid: { count: prepaidOrders.length, amount: calculateTotal(prepaidOrders) },
      cod: { count: codOrders.length, amount: calculateTotal(codOrders) },
      dhl: { count: dhlOrders.length, amount: calculateTotal(dhlOrders) },
      reservation: { count: reservationOrders.length, amount: calculateTotal(reservationOrders) },
    };
  }, [allOrders]);

  // Calculate Bot KPIs and status statistics
  const botKpis = useMemo(() => {
    const today = new Date();
    const todayOrders = allOrders.filter((o) => {
      const orderDate = toDate(o.created_at);
      return orderDate.toDateString() === today.toDateString();
    });

    // Status counts
    const statusCounts = {
      pending: allOrders.filter(o => o.status === 'pending').length,
      processing: allOrders.filter(o => o.status === 'processing').length,
      completed: allOrders.filter(o => o.status === 'completed').length,
      failed: allOrders.filter(o => o.status === 'failed').length,
      partial: allOrders.filter(o => o.status === 'partial').length,
      orderedAwaitingPayment: allOrders.filter(o => o.status === 'ordered_awaiting_payment').length,
      reservation: allOrders.filter(o => o.status === 'reservation').length,
      productUnavailable: allOrders.filter(o => o.status === 'product_unavailable').length,
    };

    // Today's stats
    const todayCompleted = todayOrders.filter(o => o.status === 'completed').length;
    const todayErrors = todayOrders.filter(o => o.status === 'failed' || o.status === 'partial').length;

    // Effectiveness = completed / (completed + failed + partial) * 100
    const totalProcessed = statusCounts.completed + statusCounts.failed + statusCounts.partial;
    const effectiveness = totalProcessed > 0 
      ? Math.round((statusCounts.completed / totalProcessed) * 100) 
      : 100;

    // Last 7 days for chart
    const last7Days: { date: string; completed: number; errors: number; total: number }[] = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      const dateStr = date.toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric' });
      
      const dayOrders = allOrders.filter(o => {
        const orderDate = toDate(o.created_at);
        return orderDate.toDateString() === date.toDateString();
      });
      
      last7Days.push({
        date: dateStr,
        completed: dayOrders.filter(o => o.status === 'completed').length,
        errors: dayOrders.filter(o => o.status === 'failed' || o.status === 'partial').length,
        total: dayOrders.length,
      });
    }

    return {
      statusCounts,
      today: {
        total: todayOrders.length,
        completed: todayCompleted,
        errors: todayErrors,
      },
      effectiveness,
      chart: last7Days,
      queueSize: queueStatus?.total_pending || 0,
      botState: botStatus?.state || 'idle',
    };
  }, [allOrders, botStatus, queueStatus]);

  // Find max value for chart scaling
  const chartMax = Math.max(...botKpis.chart.map(d => d.total), 1);

  // Format currency
  const formatCurrency = (amount: number) => 
    new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(amount);

  return (
    <div className="dashboard kamar-theme">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-left">
          <h1>✨ Dashboard Silvia</h1>
        </div>
      </div>

      {/* ========================================== */}
      {/* SECTION 1: Pozostało do zamówienia */}
      {/* ========================================== */}
      <section className="remaining-orders-section">
        {remainingStats.total === 0 ? (
          <div className="celebration-banner">
            <span className="celebration-icon">🏝️</span>
            <span className="celebration-text">Wszystko zamówione!</span>
          </div>
        ) : (
          <div className="remaining-orders-container">
            {/* Header */}
            <div className="remaining-header">
              <div className="remaining-title">
                <Package size={20} />
                <span>Pozostało do zamówienia</span>
              </div>
              <div className="remaining-summary">
                <span className="remaining-amount">{formatCurrency(remainingStats.totalAmount)}</span>
                <span className="remaining-count">{remainingStats.total} zamówień</span>
              </div>
            </div>

            {/* Category Cards */}
            <div className="remaining-cards">
              {/* Opłacone (Prepaid) */}
              {remainingStats.prepaid.count > 0 && (
                <div className="remaining-card prepaid">
                  <div className="card-content">
                    <div className="card-label">Opłacone</div>
                    <div className="card-amount">{formatCurrency(remainingStats.prepaid.amount)}</div>
                    <div className="card-emoji">{'💳'.repeat(Math.min(remainingStats.prepaid.count, 10))}</div>
                  </div>
                  <div className="card-count">{remainingStats.prepaid.count}</div>
                </div>
              )}

              {/* Za pobraniem (COD) */}
              {remainingStats.cod.count > 0 && (
                <div className="remaining-card cod">
                  <div className="card-content">
                    <div className="card-label">Za pobraniem</div>
                    <div className="card-amount">{formatCurrency(remainingStats.cod.amount)}</div>
                    <div className="card-emoji">{'🧊'.repeat(Math.min(remainingStats.cod.count, 10))}</div>
                  </div>
                  <div className="card-count">{remainingStats.cod.count}</div>
                </div>
              )}

              {/* DHL */}
              {remainingStats.dhl.count > 0 && (
                <div className="remaining-card dhl">
                  <div className="card-content">
                    <div className="card-label">Kurier DHL</div>
                    <div className="card-amount">{formatCurrency(remainingStats.dhl.amount)}</div>
                    <div className="card-emoji">{'🚚'.repeat(Math.min(remainingStats.dhl.count, 10))}</div>
                  </div>
                  <div className="card-count">{remainingStats.dhl.count}</div>
                </div>
              )}

              {/* Rezerwacje */}
              {remainingStats.reservation.count > 0 && (
                <div className="remaining-card reservation">
                  <div className="card-content">
                    <div className="card-label">Rezerwacje</div>
                    <div className="card-amount">{formatCurrency(remainingStats.reservation.amount)}</div>
                    <div className="card-emoji">{'📅'.repeat(Math.min(remainingStats.reservation.count, 10))}</div>
                  </div>
                  <div className="card-count">{remainingStats.reservation.count}</div>
                </div>
              )}
            </div>
          </div>
        )}
      </section>

      {/* ========================================== */}
      {/* SECTION 2: Bot Status & KPIs */}
      {/* ========================================== */}
      <section className="bot-kpi-section">
        <h2 className="section-title">
          <span className="section-icon">🤖</span>
          Status Bota & KPI
        </h2>

        <div className="bot-kpi-grid">
          {/* Bot Status Card */}
          <div className="bot-status-card">
            <BotStatusIndicator />
          </div>

          {/* KPI Stats Grid */}
          <div className="kpi-stats-grid">
            {/* Completed */}
            <div className="kpi-stat completed">
              <div className="kpi-icon"><CheckCircle size={24} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{botKpis.statusCounts.completed}</div>
                <div className="kpi-label">Zakończone</div>
              </div>
            </div>

            {/* Waiting for Payment */}
            <div className="kpi-stat payment">
              <div className="kpi-icon"><CreditCard size={24} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{botKpis.statusCounts.orderedAwaitingPayment}</div>
                <div className="kpi-label">Do opłacenia</div>
              </div>
            </div>

            {/* Errors */}
            <div className="kpi-stat errors">
              <div className="kpi-icon"><XCircle size={24} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{botKpis.statusCounts.failed + botKpis.statusCounts.partial}</div>
                <div className="kpi-label">Błędy</div>
              </div>
            </div>

            {/* Queue */}
            <div className="kpi-stat queue">
              <div className="kpi-icon"><Clock size={24} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{botKpis.queueSize}</div>
                <div className="kpi-label">W kolejce</div>
              </div>
            </div>

            {/* Effectiveness */}
            <div className="kpi-stat effectiveness">
              <div className="kpi-icon"><TrendingUp size={24} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{botKpis.effectiveness}%</div>
                <div className="kpi-label">Skuteczność</div>
              </div>
            </div>

            {/* Today Stats */}
            <div className="kpi-stat today">
              <div className="kpi-icon"><Calendar size={24} /></div>
              <div className="kpi-content">
                <div className="kpi-value">{botKpis.today.total}</div>
                <div className="kpi-label">Dziś łącznie</div>
              </div>
            </div>
          </div>
        </div>

        {/* Bot Status Breakdown - all statuses in one row */}
        <div className="bot-status-breakdown">
          <h3 className="breakdown-title">Statusy zamówień bota</h3>
          <div className="status-pills">
            {Object.entries(botKpis.statusCounts).map(([status, count]) => {
              if (count === 0) return null;
              const config = getBotStatusConfig(status === 'orderedAwaitingPayment' ? 'ordered_awaiting_payment' : status);
              return (
                <div 
                  key={status} 
                  className="status-pill"
                  style={{ 
                    background: config.bg, 
                    borderColor: config.border,
                    color: config.color 
                  }}
                  title={config.desc}
                >
                  <span className="pill-icon">{config.icon}</span>
                  <span className="pill-label">{config.label}</span>
                  <span className="pill-count">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========================================== */}
      {/* SECTION 3: Chart - Last 7 Days */}
      {/* ========================================== */}
      <section className="chart-section">
        <div className="section-header-row">
          <h2><BarChart3 size={20} /> Ostatnie 7 dni</h2>
        </div>
        <div className="chart-container">
          <div className="bar-chart">
            {botKpis.chart.map((day, idx) => (
              <div key={idx} className="chart-bar-group">
                <div className="chart-bars">
                  <div 
                    className="chart-bar completed"
                    style={{ height: `${(day.completed / chartMax) * 100}%` }}
                    title={`Zakończone: ${day.completed}`}
                  />
                  <div 
                    className="chart-bar errors"
                    style={{ height: `${(day.errors / chartMax) * 100}%` }}
                    title={`Błędy: ${day.errors}`}
                  />
                </div>
                <div className="chart-label">{day.date}</div>
                <div className="chart-value">{day.total}</div>
              </div>
            ))}
          </div>
          <div className="chart-legend">
            <span className="legend-item completed">
              <span className="legend-dot"></span> Zakończone
            </span>
            <span className="legend-item errors">
              <span className="legend-dot"></span> Błędy
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}
